/** Utility functions for mobile drag-and-drop reordering, separated from React hook logic. */
import { generateKeyBetween } from 'fractional-indexing';
import { getTrueRootId } from '@/features/tasks/utils/structure/taskHierarchy';
import {
  computeTodayListReorderUpdates,
  computeMoveToTodayUpdates,
} from '@/features/today/utils/todayListReorderUtils';

/** @param {{ id?: string, data?: { current?: { task?: { id?: string } } } }} item */
const resolveTaskId = (item) => item?.data?.current?.task?.id ?? item?.id;

/** @param {Array<{ id: string, [key: string]: unknown }>} updates @param {{ uid: string }} user @param {Function} updateTask */
export const applyTodayTaskUpdates = (updates, user, updateTask) => {
  if (user && updateTask && updates.length > 0) {
    updates.forEach(({ id, ...fields }) => {
      updateTask(id, fields, user.uid);
    });
  }
};

export const handleDailyTaskReorder = (
  active,
  over,
  todayTasks,
  user,
  updateTask,
) => {
  const activeId = resolveTaskId(active);
  const overId = resolveTaskId(over);
  const updates = computeTodayListReorderUpdates({
    todayTasks,
    activeId,
    overId,
  });
  applyTodayTaskUpdates(updates, user, updateTask);
};

export const handleOverdueToTodayDrop = (
  active,
  over,
  todayTasks,
  allTasks,
  user,
  updateTask,
) => {
  const activeId = resolveTaskId(active);
  const overId = resolveTaskId(over);
  const activeTask =
    allTasks.find((task) => task.id === activeId) ??
    active?.data?.current?.task;
  if (!activeId || !overId || !activeTask) return;

  const updates = computeMoveToTodayUpdates({ todayTasks, activeTask, overId });
  applyTodayTaskUpdates(updates, user, updateTask);
};

export const handleNextActionToTodayDrop = handleOverdueToTodayDrop;

const getReorderIndices = (nextActions, activeId, overId) => {
  const activeIdx = (nextActions || []).findIndex((t) => t.id === activeId);
  const overIdx = (nextActions || []).findIndex((t) => t.id === overId);
  return { activeIdx, overIdx };
};

const getReorderRoots = (activeId, overId, allTasks) => {
  const tasksMap = new Map(allTasks.map((t) => [t.id, t]));
  const activeRootId = getTrueRootId(activeId, tasksMap);
  const overRootId = getTrueRootId(overId, tasksMap);
  return { activeRootId, overRootId };
};

const getNewOrderKeyForNextAction = (
  activeIdx,
  overIdx,
  overRootId,
  allTasks,
) => {
  const allTaskIds = new Set(allTasks.map((t) => t.id));
  const rootTasks = allTasks
    .filter((t) => !t.parentId || !allTaskIds.has(t.parentId))
    .sort((a, b) => {
      const keyA = a.orderKey || '';
      const keyB = b.orderKey || '';
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    });

  const overRootIndex = rootTasks.findIndex((t) => t.id === overRootId);
  if (overRootIndex === -1) return null;

  const isMovingForward = activeIdx < overIdx;
  const prevRoot = isMovingForward
    ? rootTasks[overRootIndex]
    : rootTasks[overRootIndex - 1] || null;
  const nextRoot = isMovingForward
    ? rootTasks[overRootIndex + 1] || null
    : rootTasks[overRootIndex];

  return generateKeyBetween(
    prevRoot?.orderKey || null,
    nextRoot?.orderKey || null,
  );
};

export const handleNextActionReorder = (
  active,
  over,
  nextActions,
  allTasks,
  user,
  moveTask,
) => {
  const activeId = resolveTaskId(active);
  const overId = resolveTaskId(over);
  const { activeIdx, overIdx } = getReorderIndices(
    nextActions,
    activeId,
    overId,
  );
  if (activeIdx === -1 || overIdx === -1) return;

  const { activeRootId, overRootId } = getReorderRoots(
    activeId,
    overId,
    allTasks,
  );
  if (activeRootId === overRootId) return;

  const newOrderKey = getNewOrderKeyForNextAction(
    activeIdx,
    overIdx,
    overRootId,
    allTasks,
  );
  if (newOrderKey && user && moveTask) {
    moveTask(activeRootId, { orderKey: newOrderKey }, user.uid);
  }
};
